import { QueryStatus, useQuery } from "@tanstack/react-query";
import { CheckCircle, Warning } from "@phosphor-icons/react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard, LockedContent } from "@/components/layout/locked-content";

import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import { cn, message } from "@/lib/utils";

type ConsumerThemeItem = {
  label: string;
  frequency: number;
  progressValue: number;
};

function useConsumerThemes() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.OverviewTopConsumerThemes());

  const positiveThemes = data?.analyzed_positive_themes ?? [];
  const negativeThemes = data?.analyzed_negative_themes ?? [];
  const maxFrequency = Math.max(...positiveThemes.map((i) => i.frequency), ...negativeThemes.map((i) => i.frequency), 0);

  const mapThemes = (themes: typeof positiveThemes): ConsumerThemeItem[] =>
    themes.map((item) => ({
      label: item.theme,
      frequency: item.frequency,
      progressValue: maxFrequency > 0 ? (item.frequency / maxFrequency) * 100 : 0,
    }));

  return { positiveItems: mapThemes(positiveThemes), negativeItems: mapThemes(negativeThemes), status };
}

export function ConsumersLove() {
  const { positiveItems, status } = useConsumerThemes();
  return <ConsumersLoveComponent status={status} items={positiveItems} />;
}

export function PainPoints() {
  const { negativeItems, status } = useConsumerThemes();
  return <PainPointsComponent status={status} items={negativeItems} />;
}

function ConsumersLoveComponent({ status, items }: { status: QueryStatus; items: ConsumerThemeItem[] }) {
  switch (status) {
    case "pending":
      return <ConsumerThemesSkeleton positive />;
    case "error":
      return <LockedCard title="What Consumers Love" message={message} />;
    case "success":
      return <ConsumersLoveContent items={items} />;
  }
}

function PainPointsComponent({ status, items }: { status: QueryStatus; items: ConsumerThemeItem[] }) {
  switch (status) {
    case "pending":
      return <ConsumerThemesSkeleton positive={false} />;
    case "error":
      return <LockedCard title="Pain Points to Address" message={message} />;
    case "success":
      return <PainPointsContent items={items} />;
  }
}

function ConsumersLoveContent({ items }: { items: ConsumerThemeItem[] }) {
  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <div className="rounded-md bg-success/10 p-2">
          <CheckCircle className="size-5 text-success" weight="bold" />
        </div>
        <CardTitle className="text-base font-semibold text-success">What Consumers Love</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length ? (
          <ul className="space-y-5 max-h-96 overflow-y-auto scrollbar-hidden">
            {items.map((item, i) => (
              <li key={item.label} className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-success/20 text-xs font-bold text-success">{i + 1}</div>
                  <div className="flex min-w-0 flex-1 items-center justify-between gap-2 -translate-y-1">
                    <p className="truncate text-sm font-medium text-foreground">{item.label}</p>
                    <p className="shrink-0 text-sm text-success">{item.frequency}</p>
                  </div>
                </div>
                <div className="ml-9">
                  <Progress value={item.progressValue} className="mt-1 h-1.5 w-full bg-secondary" indicatorClassName="rounded-full bg-success" />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <LockedContent message={message} />
        )}
      </CardContent>
    </Card>
  );
}

function PainPointsContent({ items }: { items: ConsumerThemeItem[] }) {
  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <div className="rounded-md bg-destructive/10 p-2">
          <Warning className="size-5 text-destructive" weight="bold" />
        </div>
        <CardTitle className="text-base font-semibold text-destructive">Pain Points to Address</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length ? (
          <ul className="space-y-5 max-h-96 overflow-y-auto scrollbar-hidden">
            {items.map((item, i) => (
              <li key={item.label} className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-xs font-bold text-destructive">{i + 1}</div>
                  <div className="flex min-w-0 flex-1 items-center justify-between gap-2 -translate-y-1">
                    <p className="truncate text-sm font-medium text-foreground">{item.label}</p>
                    <p className="shrink-0 text-sm text-destructive">{item.frequency}</p>
                  </div>
                </div>
                <div className="ml-9">
                  <Progress value={item.progressValue} className="mt-1 h-1.5 w-full bg-secondary" indicatorClassName="rounded-full bg-destructive" />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <LockedContent message={message} />
        )}
      </CardContent>
    </Card>
  );
}

function ConsumerThemesSkeleton({ positive }: { positive: boolean }) {
  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <div className={cn("rounded-md p-2", positive ? "bg-success/10" : "bg-destructive/10")}>
          <Skeleton className="size-5 rounded-sm" />
        </div>
        <CardTitle className={cn("text-base font-semibold", positive ? "text-success" : "text-destructive")}>
          <Skeleton className="h-6 w-40 rounded-md" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="space-y-2">
            <div className="flex items-center gap-3">
              <Skeleton className="size-6 rounded-full" />
              <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <Skeleton className="h-4 w-32 rounded-md" />
                <Skeleton className="h-4 w-10 rounded-md" />
              </div>
            </div>
            <div className="ml-9 space-y-1">
              <Skeleton className="h-3 w-16 rounded-md" />
              <Skeleton className="h-2 w-full rounded-full" />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
